import { useEffect, useState } from 'react';
import { X, DownloadSimple, MagnifyingGlassPlus, MagnifyingGlassMinus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logError } from '@/utils/logger';

/**
 * Visionneuse plein écran d'image avec fond sombre + bouton téléchargement.
 * - Clic hors image ou touche Échap: ferme
 * - Bouton loupe: zoom in/out
 * - Bouton téléchargement: force le download via fetch/blob
 */
const ImageLightbox = ({ src, alt = 'Photo', filename, open, onClose }) => {
  const [zoom, setZoom] = useState(1);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setZoom(1);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === '+') setZoom((z) => Math.min(z + 0.25, 4));
      if (e.key === '-') setZoom((z) => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !src) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await fetch(src, { credentials: 'include' });
      if (!r.ok) throw new Error('fetch failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
      a.download = filename || `photo_${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (err) {
      logError('download failed', err);
      // Fallback: ouvre dans un nouvel onglet
      window.open(src, '_blank');
      toast.info('Ouverture dans un nouvel onglet');
    }
    setDownloading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center backdrop-blur-sm"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Barre d'actions */}
      <div
        className="absolute top-4 right-4 flex items-center gap-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(z - 0.25, 0.5))}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-zoom-out"
          title="Zoom arrière"
        >
          <MagnifyingGlassMinus size={20} />
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(z + 0.25, 4))}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-zoom-in"
          title="Zoom avant"
        >
          <MagnifyingGlassPlus size={20} />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="h-10 px-4 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50"
          data-testid="lightbox-download"
          title="Télécharger"
        >
          <DownloadSimple size={18} weight="bold" />
          {downloading ? 'Téléchargement...' : 'Télécharger'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="h-10 w-10 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
          data-testid="lightbox-close"
          title="Fermer (Échap)"
        >
          <X size={22} weight="bold" />
        </button>
      </div>

      {/* Image */}
      <div className="max-w-[90vw] max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <img
          src={src}
          alt={alt}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.2s' }}
          className="max-w-full max-h-[85vh] object-contain select-none rounded"
          draggable={false}
        />
      </div>

      {/* Légende zoom */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs bg-white/10 rounded-full px-3 py-1"
        onClick={(e) => e.stopPropagation()}
      >
        {Math.round(zoom * 100)}% — Échap pour fermer
      </div>
    </div>
  );
};

export default ImageLightbox;
